import { Link } from 'react-router-dom';
import { getUnitByName } from '../../shared/unitInfo';
import { sortByAttributeOrder } from '../../shared/tierlistOrder';
import SkeletonImage from '../SkeletonImage';
import { NukeHighlightChip } from './NukeHighlightChip';

const NUKE_CATEGORIES = [
    {
        key: 'arts',
        title: 'Arts Nukers',
        blurb: 'Fast, hard-hitting Arts',
    },
    {
        key: 'equip',
        title: 'Equip Nukers',
        blurb: 'High DMG passives with slots to hit hard',
    },
    {
        key: 'support',
        title: 'Nuke Supports',
        blurb: 'Buffs, debuffs, wave clear, burst Arts',
    },
];

/**
 * Each entry is a unit name string or { name, highlights }.
 * Highlights mirror the spreadsheet notes on what each unit excels at.
 */
const NUKE_TIERS = [
    {
        id: 'apex',
        label: 'Apex',
        units: {
            arts: [
                {
                    name: 'Saito Hajime',
                    highlights: ['High Self Buff', 'High RES Down', 'Solo Nuker', 'High Accuracy', 'One-Hit'],
                },
                {
                    name: 'Hero Saitama',
                    highlights: ['High Self Buff', 'Solo Nuker', 'DEF Ignore'],
                },
                {
                    name: 'Okarun',
                    highlights: ['High Self Buff', 'High RES Down', 'Solo Nuker'],
                },
            ],
            equip: [
                {
                    name: 'Hero Saitama',
                    highlights: ['High Self Buff'],
                },
            ],
            support: [
                {
                    name: 'Momon',
                    highlights: ['Buff Passive', 'High DMG Up', 'High RES Down'],
                },
                {
                    name: 'Summer Luvia & Rin',
                    highlights: ['Arts Passive', 'High DMG Up', 'High RES Down'],
                },
                {
                    name: 'Draken',
                    highlights: ['Buff Passive', 'Arts Passive', 'Slot Mule'],
                },
            ],
        },
    },
    {
        id: 'strong',
        label: 'Strong',
        units: {
            arts: [
                {
                    name: 'Saitama',
                    highlights: ['High Self Buff', 'Solo Nuker', 'One-Hit'],
                },
                {
                    name: 'Sunraku',
                    highlights: ['Self CRI', 'High SCRI', 'Solo Nuker'],
                },
                {
                    name: 'Ultimate Paladin Roy',
                    highlights: ['High RES Down'],
                },
            ],
            equip: [
                {
                    name: 'Delta',
                    highlights: ['High Self Buff'],
                },
                {
                    name: 'Cometstreak Valerie',
                    highlights: ['High Self Buff'],
                },
                {
                    name: 'Draken',
                    highlights: ['Buff Passive'],
                },
                {
                    name: 'Duke',
                    highlights: ['High Self Buff'],
                },
            ],
            support: [
                {
                    name: 'Genos',
                    highlights: ['Player EXP', 'Slot Mule'],
                },
                {
                    name: 'Summer Miyu',
                    highlights: ['Jump Start', 'High DMG Up', 'Arts Battery'],
                },
                {
                    name: 'Okarun',
                    highlights: ['Jump Start', 'High DMG Up'],
                },
                {
                    name: 'Beta',
                    highlights: ['Buff Passive', 'Slot Mule'],
                },
                {
                    name: 'Igris',
                    highlights: ['High DMG Up', 'High RES Down', 'Arts Battery'],
                },
                {
                    name: 'New Years Milim',
                    highlights: ['Buff Passive', 'Arts Passive', 'Slot Mule'],
                },
            ],
        },
    },
    {
        id: 'average',
        label: 'Average',
        units: {
            arts: [
                {
                    name: 'Makoto Shishio',
                    highlights: ['High RES Down'],
                },
                {
                    name: 'Stark',
                    highlights: ['High Self Buff', 'High RES Down', 'Solo Nuker'],
                },
                {
                    name: 'Mikoto Misaka',
                    highlights: ['High RES Down'],
                },
                {
                    name: 'Marisa Kirisame',
                    highlights: ['HP Scaling'],
                },
                {
                    name: 'Summer Cestina',
                    highlights: ['HP Scaling'],
                },
            ],
            equip: [
                {
                    name: 'Momon',
                    highlights: ['Buff Passive'],
                },
                {
                    name: 'Mikey',
                    highlights: ['High Self Buff'],
                },
                {
                    name: 'Genos',
                    highlights: ['Player EXP'],
                },
                {
                    name: 'Erza',
                    highlights: ['Self CRI'],
                },
            ],
            support: [
                {
                    name: 'Gray Fullbuster',
                    highlights: ['Jump Start', 'High DMG Up', 'Arts Battery'],
                },
                {
                    name: 'Sublime Supernova Liza',
                    highlights: ['Slot Mule'],
                },
                {
                    name: 'War Hero Fen',
                    highlights: ['Arts Passive', 'Slot Mule'],
                },
                {
                    name: 'Takemichi',
                    highlights: ['Player EXP', 'Slot Mule', 'Arts Battery'],
                },
            ],
        },
    },
    {
        id: 'niche',
        label: 'Niche / Bad',
        units: {
            arts: [
                {
                    name: 'Santa Shion',
                    highlights: ['Solo Nuker', 'High Accuracy', 'One-Hit'],
                },
                {
                    name: 'Ragsherum',
                    highlights: ['Self CRI', 'Solo Nuker', 'One-Hit'],
                },
                {
                    name: 'Duran',
                    highlights: ['Reflect Nuker'],
                },
            ],
            equip: [],
            support: [
                {
                    name: 'Forte',
                    highlights: ['Arts Passive', 'High DMG Up', 'Slot Mule'],
                },
                {
                    name: 'Fen (Dark)',
                    highlights: ['Arts Passive', 'Slot Mule'],
                },
                {
                    name: 'Melia (Dark)',
                    highlights: ['Time Stop'],
                },
                {
                    name: 'Melia (Water)',
                    highlights: ['Time Stop'],
                },
            ],
        },
    },
];

const HIGHLIGHT_VISIBLE_LIMIT = 5;

function normalizeNukeEntry(entry) {
    if (typeof entry === 'string') {
        return { name: entry, highlights: [] };
    }

    return {
        name: entry.name,
        highlights: entry.highlights || [],
    };
}

function resolveNukeUnit(entry) {
    const { name, highlights } = normalizeNukeEntry(entry);
    const unit = getUnitByName(name);
    const thumb = unit?.image?.thumbsuper
        || unit?.image?.thumbawk
        || unit?.image?.thumb5
        || '';

    return {
        name: unit?.name || name,
        thumb,
        attribute: unit?.attribute || '',
        highlights,
    };
}

function NukeUnit({ unit, categoryKey }) {
    const hasOverflow = unit.highlights.length > HIGHLIGHT_VISIBLE_LIMIT;
    const overflowCount = unit.highlights.length - HIGHLIGHT_VISIBLE_LIMIT;

    return (
        <article className={`gs-nuke-unit gs-nuke-unit--${categoryKey}${hasOverflow ? ' gs-nuke-unit--expandable' : ''}`}>
            <Link to={`/units/${unit.name}`} className="gs-nuke-unit__link">
                <SkeletonImage
                    src={unit.thumb}
                    alt={unit.name}
                    loading="lazy"
                    frameClassName="gs-skeleton-frame--tierlist"
                />
                <span className="gs-nuke-unit__name">{unit.name}</span>
            </Link>
            {unit.highlights.length > 0 && (
                <ul
                    className={`gs-nuke-unit__highlights${hasOverflow ? ' is-collapsible' : ''}`}
                    aria-label={`${unit.name} strengths`}
                >
                    {unit.highlights.map((highlight, index) => (
                        <NukeHighlightChip
                            key={`${highlight}-${index}`}
                            label={highlight}
                            tipId={`nuke-tip-${categoryKey}-${unit.name}-${index}`}
                            className={
                                hasOverflow && index >= HIGHLIGHT_VISIBLE_LIMIT
                                    ? 'gs-nuke-unit__highlight--overflow'
                                    : ''
                            }
                        />
                    ))}
                    {hasOverflow && (
                        <li className="gs-nuke-unit__highlight gs-nuke-unit__highlight--more" aria-hidden="true">
                            +{overflowCount} more
                        </li>
                    )}
                </ul>
            )}
        </article>
    );
}

function NukeCategoryColumn({ category, units }) {
    const resolved = sortByAttributeOrder(units.map(resolveNukeUnit));

    return (
        <div className={`gs-nuke-col gs-nuke-col--${category.key}`}>
            <div className="gs-nuke-col__head">
                <h3 className="gs-nuke-col__title">{category.title}</h3>
                <p className="gs-nuke-col__blurb">{category.blurb}</p>
            </div>
            {resolved.length > 0 ? (
                <div className="gs-tierlist-grid gs-nuke-col__grid">
                    {resolved.map((unit) => (
                        <NukeUnit
                            key={`${category.key}-${unit.name}`}
                            unit={unit}
                            categoryKey={category.key}
                        />
                    ))}
                </div>
            ) : (
                <p className="gs-nuke-col__empty">None listed</p>
            )}
        </div>
    );
}

export const CreateNukeTable = () => {
    return (
        <div className="gs-nuke">
            <div className="gs-nuke__tiers">
                {NUKE_TIERS.map((tier) => (
                    <section
                        key={tier.id}
                        className={`gs-nuke-tier gs-nuke-tier--${tier.id}`}
                        aria-labelledby={`nuke-tier-${tier.id}`}
                    >
                        <h2 id={`nuke-tier-${tier.id}`} className="gs-nuke-tier__title">
                            {tier.label}
                        </h2>
                        <div className="gs-nuke-tier__cols">
                            {NUKE_CATEGORIES.map((category) => (
                                <NukeCategoryColumn
                                    key={category.key}
                                    category={category}
                                    units={tier.units[category.key]}
                                />
                            ))}
                        </div>
                    </section>
                ))}
            </div>
        </div>
    );
};
